import clsx from 'clsx'

/**
 * Default a date-range picker to the last day of data, not the full earliest-latest span --
 * the full span is what was making TIMS gaps / geotags reports slow to load by default.
 * The picker's min/max floor should still come from the raw earliest/latest.
 */
export function defaultOneDayRange(range: { earliest: string | null; latest: string | null }): { from: string; to: string } {
  if (!range.earliest || !range.latest) return { from: '', to: '' }
  const to = new Date(range.latest)
  const from = new Date(to.getTime() - 24 * 60 * 60 * 1000)
  const earliest = new Date(range.earliest)
  const clampedFrom = from < earliest ? earliest : from
  return { from: clampedFrom.toISOString().slice(0, 10), to: range.latest.slice(0, 10) }
}

/** Tailwind UI-style class recipes scoped to the Reports surface. */
export const reportCard =
  'rounded-xl border border-slate-200/80 bg-white shadow-sm ring-1 ring-gray-900/5'

export const reportCardPad = 'p-6'
export const reportCardPadSm = 'p-4'

export const reportSectionTitle = 'text-lg font-semibold text-slate-900'
export const reportSectionSub = 'mt-1 max-w-prose text-sm text-slate-500'

export const reportCardTitle = 'text-sm font-semibold text-slate-900'
export const reportCardSub = 'text-xs text-slate-500'

export const reportEyebrow =
  'font-mono text-xs font-semibold uppercase tracking-wide text-slate-500'

export const reportLoading =
  'flex items-center justify-center p-12 text-sm text-slate-500'

export const reportSegment = 'inline-flex rounded-lg bg-slate-100 p-1'

export function reportSegmentBtn(active: boolean): string {
  return clsx(
    'rounded-md px-3.5 py-1.5 text-sm font-medium transition-all',
    active
      ? 'bg-white text-slate-900 shadow-sm ring-1 ring-gray-900/5'
      : 'text-slate-500 hover:text-slate-700',
  )
}

export const reportTableWrap =
  'overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5'

export const reportSidebarActive =
  'border-l-2 border-blue-700 bg-blue-50 text-blue-700'

/* ------------------------------------------------------------------ table vocabulary
 * One recipe for every dense data table on the Reports surface (and the Breakdowns log,
 * which is the same kind of table). Defined once so GeoTags, MajorEvents, RoundTrip's
 * visits and the raw-data ReportTable stop each inventing their own header grey.
 */

/** Scroll container that makes the sticky header work: cap the height, scroll inside. */
export const reportTableScroll = 'max-h-[65vh] overflow-auto overscroll-contain'

/** Header typography. Sticky positioning lives on each th (reportTh), not the thead --
 *  sticky on a thead element is still unreliable in Safari. */
export const reportThead =
  'text-left font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500'

/** Sticky header cell. Near-opaque + blur so rows sliding under it stay legible. */
export const reportTh =
  'sticky top-0 z-10 whitespace-nowrap bg-slate-50/95 px-3 py-2.5 backdrop-blur-sm'

/** Row rhythm: hairline dividers + faint zebra + a hover that reads instantly. */
export const reportTr =
  'border-b border-slate-100 last:border-b-0 even:bg-slate-50/40 ' +
  'transition-colors duration-100 hover:bg-blue-50/40'

export const reportTd = 'whitespace-nowrap px-3 py-2'

/** Staggered entrance for a freshly loaded/filtered row set. Cap the delay so deep rows
 *  never sit invisible; the reduced-motion rule in index.css zeroes all of it. */
export function rowEntrance(index: number): { className: string; delay: string } {
  return { className: 'animate-row-in', delay: `${Math.min(index, 12) * 16}ms` }
}

/* --------------------------------------------------------------- control vocabulary */

/** Filter-bar input (dates, selects, text). Blue focus ring, same as the rest of Reports. */
export const reportInput =
  'rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] text-slate-700 ' +
  'shadow-sm transition-colors focus:border-blue-500 focus:outline-none ' +
  'focus:ring-2 focus:ring-blue-500/20'

export const reportBtnPrimary =
  'inline-flex items-center justify-center gap-1.5 rounded-md bg-slate-900 px-3 text-[12px] ' +
  'font-medium text-white shadow-sm transition-[background-color,transform] duration-100 ' +
  'hover:bg-slate-700 active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50'

export const reportBtnGhost =
  'inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white ' +
  'px-3 text-[12px] font-medium text-slate-700 shadow-sm transition-[border-color,color,transform] ' +
  'duration-100 hover:border-slate-300 hover:text-slate-900 active:scale-[0.97] ' +
  'disabled:pointer-events-none disabled:opacity-50'

/** Anchored dropdown panel (export menu, column picker). */
export const reportMenuPanel =
  'absolute right-0 z-20 mt-1 animate-menu-in rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5'

/** Status pill: rounded-full, 10% tint, dark text — the console's printed-pill recipe. */
export function reportPill(tone: 'ok' | 'warn' | 'danger' | 'info' | 'neutral'): string {
  const tones = {
    ok: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    warn: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    danger: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    info: 'bg-sky-50 text-sky-700 ring-sky-600/20',
    neutral: 'bg-slate-100 text-slate-600 ring-slate-500/10',
  } as const
  return clsx(
    'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5',
    'text-[10.5px] font-medium ring-1 ring-inset',
    tones[tone],
  )
}
